import React, { useState, useEffect } from 'react';
import { api } from '../api';

function AudioLibrary({ user }) {
  const [audioFiles, setAudioFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    loadAudio();
  }, []);

  const loadAudio = async () => {
    try {
      const data = await api.getAudioFiles();
      setAudioFiles(data);
    } catch (error) {
      console.error('Failed to load audio:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (file) => {
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.wav') && !file.name.toLowerCase().endsWith('.mp3')) {
      alert('Only WAV and MP3 files are supported');
      return;
    }

    setUploading(true);
    try {
      await api.uploadAudio(file);
      loadAudio();
    } catch (error) {
      alert(error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this audio file?')) return;
    
    try {
      await api.deleteAudio(id);
      loadAudio();
    } catch (error) {
      alert(error.message);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div>
      <h1 style={{ fontSize: '2rem', fontWeight: 600, marginBottom: '1.5rem' }}>Audio Library</h1>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Upload Audio</h3>
          <p className="card-description">Upload WAV or MP3 files (will be converted to 8kHz mono for Asterisk)</p>
        </div>
        <div
          className={`file-upload ${dragActive ? 'dragging' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => document.getElementById('audio-file-input').click()}
          data-testid="audio-upload-dropzone"
        >
          {uploading ? (
            <div>
              <div className="spinner" style={{ margin: '0 auto 1rem' }}></div>
              <p>Uploading and converting...</p>
            </div>
          ) : (
            <div>
              <svg style={{ width: '48px', height: '48px', margin: '0 auto 1rem', color: 'hsl(var(--baby-blue))' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p>Drop audio file here or click to browse</p>
              <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.5rem' }}>
                WAV or MP3 files only
              </p>
            </div>
          )}
        </div>
        <input
          id="audio-file-input"
          type="file"
          accept=".wav,.mp3"
          style={{ display: 'none' }}
          onChange={(e) => handleFileSelect(e.target.files[0])}
        />
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Your Audio Files</h3>
        </div>
        {audioFiles.length === 0 ? (
          <p style={{ color: 'hsl(var(--muted-foreground))' }}>No audio files yet. Upload your first audio file!</p>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Filename</th>
                  <th>Duration</th>
                  <th>Size</th>
                  <th>Uploaded</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {audioFiles.map(audio => (
                  <tr key={audio.id}>
                    <td>{audio.original_filename}</td>
                    <td>{formatDuration(audio.duration)}</td>
                    <td>{formatFileSize(audio.file_size)}</td>
                    <td style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
                      {new Date(audio.uploaded_at).toLocaleDateString()}
                    </td>
                    <td>
                      <button
                        className="btn btn-danger"
                        style={{ padding: '0.25rem 0.75rem', fontSize: '0.75rem' }}
                        onClick={() => handleDelete(audio.id)}
                        data-testid={`btn-delete-audio-${audio.id}`}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default AudioLibrary;